import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Film, Heart, Volume2 } from "lucide-react";
import { api } from "../lib/api";
import StorefrontHeader from "../components/StorefrontHeader";
import { ReelStorefrontCard } from "./Storefront";
import useEmbedResize from "../hooks/useEmbedResize";

/**
 * Format hub: vertical-video competitions (TikTok / Stories style).
 * If there is exactly one live reel we skip the list and jump straight into
 * the player — matches the "click Reels → go straight to videos" flow.
 */
export default function StorefrontReels() {
  useEmbedResize();
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.publicListReels().then(setReels).catch(() => setReels([])).finally(() => setLoading(false));
  }, []);

  if (!loading && reels.length === 1) {
    // Preserve embed/query params so iframe embed keeps working.
    const target = `/storefront/reels/${reels[0].id}${window.location.search || ""}`;
    return <Navigate to={target} replace />;
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="storefront-reels-page">
      <StorefrontHeader active="reels" />

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-16">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#d0473e] font-semibold">
            <Film size={12} /> Video competitions
          </div>
          <h1 className="text-3xl sm:text-5xl font-semibold tracking-tight mt-3 leading-tight text-[#111]">
            Watch. Vote. Drop.
          </h1>
          <p className="text-base sm:text-lg text-[#6D7175] mt-4 leading-relaxed">
            Vertical video pitches — tap the heart on the ones you want us to launch.
            Top videos at the deadline go straight to pre-order.
          </p>
          <div className="mt-6 flex items-center gap-4 text-xs text-[#6D7175]">
            <span className="inline-flex items-center gap-1.5"><Heart size={12} className="text-[#d0473e]" /> Tap the heart to vote</span>
            <span className="inline-flex items-center gap-1.5"><Volume2 size={12} className="text-[#d0473e]" /> Tap video for sound</span>
          </div>
        </div>

        <section className="mt-12" data-testid="reels-list">
          {loading ? (
            <div className="lp-card p-12 text-center text-sm text-gray-500">Loading reels…</div>
          ) : reels.length === 0 ? (
            <div className="lp-card p-16 text-center" data-testid="reels-empty">
              <div className="w-14 h-14 rounded-2xl bg-rose-50 border border-rose-100 flex items-center justify-center mx-auto mb-4">
                <Film size={22} className="text-rose-500" />
              </div>
              <h3 className="text-lg font-medium tracking-tight">No live reels right now</h3>
              <p className="text-sm text-gray-500 mt-2 max-w-sm mx-auto">
                Check back soon — we drop fresh reels regularly.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="text-base font-medium tracking-tight">Now playing</h2>
                <span className="text-xs text-gray-500">{reels.length} live</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {reels.map((r) => (
                  <ReelStorefrontCard key={r.id} reel={r} />
                ))}
              </div>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
